import { useCallback, useEffect, useRef, useState } from 'react';
import { useSimStore } from '../../store/useSimStore';
import { useBroadcastSync } from '../../sync/useBroadcastSync';
import { useWebSocketSync } from '../../sync/useWebSocketSync';
import { evaluateAlarms, initializeAudioContext, playSpO2Beep, playAlarmTone, playNibpCuffCycle, playAudioClip, playShockSequence, playPneumothoraxRelease } from '../../engine/alarmEngine';
import { computeVentNumerics, evaluateVentAlarms, parseAgeYears } from '../../engine/ventEngine';
import WaveformCanvas from './WaveformCanvas';
import AlarmBar from './AlarmBar';
import MediaOverlay from './MediaOverlay';
import CognitiveAidOverlay from './CognitiveAidOverlay';
import MonitorTrends from './MonitorTrends';
import styles from './MonitorDisplay.module.css';

// ── GE Carescape numeric-parameter box ───────────────────────────────────────
// Header row (label · unit · alarm limits), a large colour-coded value, and an
// optional row of sub-values (e.g. ST leads, CO₂ ET/FI/RR).
function GeBox({ label, unit, limits, color, value, sub, subValues, children, big = true, tall = false }) {
  return (
    <div className={`${styles.geBox} ${tall ? styles.geBoxTall : ''}`} style={{ '--accent': color }}>
      <div className={styles.geBoxHead}>
        <span className={styles.geBoxLabel} style={{ color }}>{label}</span>
        {unit && <span className={styles.geBoxUnit}>{unit}</span>}
        {limits && <span className={styles.geBoxLimits}>{limits}</span>}
      </div>
      {value !== undefined && (
        <div className={styles.geBoxMain}>
          <span className={big ? styles.geBoxValue : styles.geBoxValueSm} style={{ color }}>{value}</span>
          {sub && <span className={styles.geBoxSub} style={{ color }}>{sub}</span>}
        </div>
      )}
      {subValues && (
        <div className={styles.geSubRow}>
          {subValues.map((s, i) => (
            <span key={i} className={styles.geSubVal}>
              <span className={styles.geSubLabel} style={{ color: s.color }}>{s.label}</span>
              <span className={styles.geSubNum} style={{ color: s.color }}>{s.value}</span>
            </span>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}

const GE_MENU = ['Alarms\nSetup', 'Monitor\nSetup', 'Procedures', 'Data &\nPages', 'Trends',
                 'Print\nWaveforms', 'Freeze/\nSnapshot'];

// Alarm priority for re-sorting the combined vitals + ventilator alarm list.
const ALARM_PRIORITY = { crisis: 3, warning: 2, advisory: 1 };

// Map channel toggle keys → vital param keys whose alarms are suppressed when
// that display channel is turned off. Module-scope so the alarm interval can
// close over a stable reference.
const CHANNEL_VITAL_KEYS = {
  abp:   ['ABP_Sys', 'ABP_Dia'],
  cvp:   ['CVP'],
  etco2: ['EtCO2'],
  temp:  ['Temp'],
};

export default function MonitorDisplay() {
  const store = useSimStore();
  const syncMode = import.meta.env.VITE_SYNC_MODE || 'broadcast';
  const [audioContext, setAudioContext] = useState(null);
  const [localCycling, setLocalCycling] = useState(false);
  const [nibpAuto, setNibpAuto] = useState(false);   // NIBP Auto Start → q2.5 min
  const [showTrends, setShowTrends] = useState(false);

  // Local alarm-audio silence (click the alarm bar). Pauses alarm TONES for 2 min
  // while the visual alarm stays up — matches a real monitor's silence button.
  // Local to this monitor (not synced); combines with the global audioMuted.
  const ALARM_SILENCE_MS = 120000;
  const [alarmSilencedUntil, setAlarmSilencedUntil] = useState(0);
  const [, forceAlarmTick] = useState(0);
  const alarmSilencedUntilRef = useRef(0);
  alarmSilencedUntilRef.current = alarmSilencedUntil;
  const toggleAlarmSilence = () =>
    setAlarmSilencedUntil((cur) => (cur > Date.now() ? 0 : Date.now() + ALARM_SILENCE_MS));
  useEffect(() => {
    if (!alarmSilencedUntil) return;
    const id = setInterval(() => {
      if (Date.now() >= alarmSilencedUntil) setAlarmSilencedUntil(0);
      else forceAlarmTick((n) => n + 1);
    }, 500);
    return () => clearInterval(id);
  }, [alarmSilencedUntil]);
  const alarmSilenced = alarmSilencedUntil > Date.now();
  const alarmSilenceRemaining = alarmSilenced ? Math.ceil((alarmSilencedUntil - Date.now()) / 1000) : 0;

  // Preview mode: the instructor's MiniMonitor loads /monitor?view=monitor.
  // Audio must NEVER play from the instructor window — only the real monitor —
  // so the preview instance is permanently silenced here.
  const isPreview = new URLSearchParams(window.location.search).get('view') === 'monitor';
  const effectiveMuted = store.audioMuted || isPreview;

  useBroadcastSync(false);
  // Two-device (LAN) mode: also receive from the WebSocket relay (no-op unless active).
  useWebSocketSync(false);

  useEffect(() => {
    const handleInitAudio = () => {
      if (!audioContext) {
        try { setAudioContext(initializeAudioContext()); }
        catch (e) { console.error('Failed to initialize audio:', e); }
      }
    };
    document.addEventListener('click', handleInitAudio, { once: true });
    return () => document.removeEventListener('click', handleInitAudio);
  }, [audioContext]);

  // NIBP is owned by the monitor (manual + auto q2.5 min), so cycling is local.
  const isCycling = localCycling;

  // Mirror cycling state into a ref so the alarm interval (set up once) reads
  // the latest value without being torn down on every render.
  const isCyclingRef = useRef(isCycling);
  isCyclingRef.current = isCycling;

  // Evaluate alarms every 500ms. The interval is created ONCE and reads fresh
  // state via getState() — depending on the whole-store `store` ref here would
  // recreate the timer on every render (≈20×/s during transitions), starving
  // the 500ms callback so alarms never update mid-transition.
  useEffect(() => {
    const interval = setInterval(() => {
      const s = useSimStore.getState();
      const skipKeys = isCyclingRef.current ? ['NIBP_Sys', 'NIBP_Dia'] : [];
      // Suppress alarms for vitals whose display channel is turned off
      Object.entries(CHANNEL_VITAL_KEYS).forEach(([channel, keys]) => {
        if (!s.visibleChannels[channel]) skipKeys.push(...keys);
      });
      let alarms = evaluateAlarms(s.vitals, { skipKeys });
      // When the ventilator screen is in use, add airway-pressure / volume
      // alarms so respiratory scenarios trigger ventilatory alarms too.
      if (s.splitScreen && s.intubated) {
        const ventNum = computeVentNumerics(s.ventSettings, parseAgeYears(s.patientAge));
        const ventAlarms = evaluateVentAlarms(ventNum);
        if (ventAlarms.length) {
          alarms = [...alarms, ...ventAlarms].sort(
            (a, b) => ALARM_PRIORITY[b.level] - ALARM_PRIORITY[a.level]
          );
        }
      }
      s.setAlarms(alarms);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // One-shot audio cues fired by the instructor (defib/cardioversion/needle).
  // Played here on the MONITOR window only; the preview iframe is muted above.
  const lastCueIdRef = useRef(null);
  useEffect(() => {
    const cue = store.audioCue;
    if (!cue || cue.id === lastCueIdRef.current) return;
    // Ignore stale cues (e.g. replayed from the synced snapshot on monitor load)
    if (Date.now() - cue.id > 5000) { lastCueIdRef.current = cue.id; return; }
    // Not playable yet (muted, or audio not unlocked by a first click)? Leave the
    // cue UNstamped — this effect re-runs when audioContext/mute changes, so a cue
    // that arrived seconds before the unlocking click still plays. Stamping before
    // this gate permanently swallowed those cues; the 5 s staleness guard above
    // bounds how late a deferred cue can fire.
    if (effectiveMuted || !audioContext) return;
    lastCueIdRef.current = cue.id;
    if (cue.type === 'defibrillation' || cue.type === 'cardioversion') {
      playShockSequence(audioContext, cue.type);
    } else if (cue.type === 'needleRelease') {
      playPneumothoraxRelease(audioContext);
    }
  }, [store.audioCue, audioContext, effectiveMuted]);

  // Shock tally — running count + times of every defib/cardioversion delivered,
  // shown on the monitor. Visual only (not gated by mute), so it also appears in
  // the muted instructor preview. cue.id is the shock timestamp (button press).
  const [shocks, setShocks] = useState([]); // [{ type, at, energy }]
  const lastShockCueRef = useRef(null);
  useEffect(() => {
    const cue = store.audioCue;
    if (!cue || cue.id === lastShockCueRef.current) return;
    if (cue.type !== 'defibrillation' && cue.type !== 'cardioversion') return;
    lastShockCueRef.current = cue.id;
    if (Date.now() - cue.id > 5000) return; // ignore a stale cue replayed on load
    setShocks((prev) => [...prev, { type: cue.type, at: cue.id, energy: cue.energy }].slice(-40));
  }, [store.audioCue]);

  // CPR coaching voice — every 10 s while a CPR quality mode is active.
  //   High-quality CPR → "Good compressions"; low-quality → "Push harder".
  useEffect(() => {
    const clip =
      store.rhythm === 'cpr_hq' ? '/audio/good_compressions.mp3' :
      store.rhythm === 'cpr_lq' ? '/audio/push_harder.mp3' :
      null;
    if (!clip || effectiveMuted) return;
    playAudioClip(clip);                                  // immediate feedback
    const id = setInterval(() => playAudioClip(clip), 10000);
    return () => clearInterval(id);
  }, [store.rhythm, effectiveMuted]);

  // Refs so the manual-cycle timeout closure always sees fresh values
  const audioContextRef = useRef(audioContext);
  const effectiveMutedRef = useRef(effectiveMuted);
  useEffect(() => { audioContextRef.current = audioContext; }, [audioContext]);
  useEffect(() => { effectiveMutedRef.current = effectiveMuted; }, [effectiveMuted]);

  // One NIBP measurement: cuff sound → 21 s inflate/measure → capture the
  // current NIBP as the displayed reading. Shared by the manual button and the
  // q2.5-min auto-cycle. Guarded so overlapping presses/cycles are ignored.
  const localCyclingTimeoutRef = useRef(null);
  const cyclingRef = useRef(false);
  cyclingRef.current = localCycling;
  const runNibpCycle = useCallback(() => {
    if (cyclingRef.current) return;
    if (audioContextRef.current && !effectiveMutedRef.current) {
      playNibpCuffCycle(audioContextRef.current);
    }
    setLocalCycling(true);
    localCyclingTimeoutRef.current = setTimeout(() => {
      const { vitals } = useSimStore.getState();
      useSimStore.getState().setNibpLastMeasured(
        Math.round(vitals.NIBP_Sys.current),
        Math.round(vitals.NIBP_Dia.current),
      );
      setLocalCycling(false);
    }, 21_000);
  }, []);

  // NIBP Auto Start: measure immediately, then every 2.5 minutes until stopped.
  useEffect(() => {
    if (!nibpAuto) return;
    runNibpCycle();
    const id = setInterval(runNibpCycle, 150_000);
    return () => clearInterval(id);
  }, [nibpAuto, runNibpCycle]);

  // Cleanup manual cycle timeout on unmount
  useEffect(() => () => clearTimeout(localCyclingTimeoutRef.current), []);

  // Rolling trend history: sample the displayed numerics every 10 s into a ring
  // buffer (~2 h). Only active channels are recorded (HR/SpO₂/NIBP always on);
  // NIBP uses the last measured cuff value. The "Trends" menu button shows it.
  const [trendSamples, setTrendSamples] = useState([]);
  useEffect(() => {
    if (isPreview) return; // the instructor preview iframe doesn't need its own trends
    const sample = () => {
      const s = useSimStore.getState();
      const v = s.vitals; const vis = s.visibleChannels || {};
      const r = (x) => (typeof x === 'number' ? Math.round(x * 10) / 10 : null);
      const g = (on, x) => (on ? r(x) : null);
      setTrendSamples((prev) => {
        const next = prev.concat({
          t: Date.now(),
          HR: r(v.HR?.current), SpO2: r(v.SpO2?.current),
          NIBP_Sys: s.nibpLastMeasured?.sys ?? null, NIBP_Dia: s.nibpLastMeasured?.dia ?? null,
          ABP_Sys: g(vis.abp, v.ABP_Sys?.current), ABP_Dia: g(vis.abp, v.ABP_Dia?.current),
          EtCO2: g(vis.etco2, v.EtCO2?.current), RR: g(vis.etco2, v.RR?.current),
          Temp: g(vis.temp, v.Temp?.current), CVP: g(vis.cvp, v.CVP?.current),
          BIS: g(vis.bis, s.bisValue), TOF: g(vis.tof, s.tofCount),
        });
        return next.length > 720 ? next.slice(next.length - 720) : next;
      });
    };
    sample();
    const id = setInterval(sample, 10_000);
    return () => clearInterval(id);
  }, [isPreview]);

  // SpO₂ pulse beep — fires once per heartbeat (R-wave), pitch tracks saturation
  const beepTimeoutRef = useRef(null);
  useEffect(() => {
    if (!audioContext || effectiveMuted) return;

    const scheduleBeep = () => {
      const { vitals, rhythm } = useSimStore.getState();
      const hr = vitals.HR?.current || 0;
      const spo2 = vitals.SpO2?.current || 99;

      // No beep during arrest/VF or HR=0
      if (hr <= 0 || rhythm === 'vf') {
        beepTimeoutRef.current = setTimeout(scheduleBeep, 500);
        return;
      }

      playSpO2Beep(audioContext, spo2);

      // Schedule next beep at the current heart rate interval
      const beatIntervalMs = (60 / hr) * 1000;
      beepTimeoutRef.current = setTimeout(scheduleBeep, beatIntervalMs);
    };

    scheduleBeep();
    return () => clearTimeout(beepTimeoutRef.current);
  }, [audioContext, effectiveMuted]);

  // IEC 60601-1-8 alarm tones — plays highest-priority active alarm
  // Repeat intervals: crisis 3s, warning 15s, advisory 30s
  const alarmTimeoutRef = useRef(null);
  useEffect(() => {
    if (!audioContext || effectiveMuted) {
      clearTimeout(alarmTimeoutRef.current);
      return;
    }

    const REPEAT_MS = { crisis: 3000, warning: 5000, advisory: 15000 };

    const scheduleAlarmTone = () => {
      const { alarms } = useSimStore.getState();
      if (alarms.length === 0) {
        // No active alarms — poll slowly
        alarmTimeoutRef.current = setTimeout(scheduleAlarmTone, 1000);
        return;
      }

      const highest = alarms[0]; // already sorted by priority
      // Alarm-audio silence: skip the tone but keep polling so it auto-resumes
      // when the 2-min pause expires (if the condition still exists).
      if (Date.now() >= alarmSilencedUntilRef.current) {
        playAlarmTone(audioContext, highest.level);
      }

      const repeatMs = REPEAT_MS[highest.level] || 15000;
      alarmTimeoutRef.current = setTimeout(scheduleAlarmTone, repeatMs);
    };

    scheduleAlarmTone();
    return () => clearTimeout(alarmTimeoutRef.current);
  }, [audioContext, effectiveMuted]);

  const v = store.vitals;
  const ch = store.visibleChannels;
  const tofCount = store.tofCount;
  const tofRatio = store.tofRatio;
  const bisValue = store.bisValue;

  // BIS colour: green = target (40-60), yellow = light (60-80), orange = deep (<40), red = awake (>80)
  const bisColor = bisValue > 80 ? '#FF1744' : bisValue > 60 ? '#FFD600' : bisValue >= 40 ? '#00E676' : '#FF9100';

  // NIBP shows the last successfully measured reading, not the live value.
  // Unreadable when: cycling (cuff occupied) OR last measured systolic < 60 mmHg.
  const nibpUnreadable = isCycling || store.nibpLastMeasured.sys < 60;
  const nibpDisplay = nibpUnreadable
    ? '--/--'
    : `${store.nibpLastMeasured.sys}/${store.nibpLastMeasured.dia}`;

  const map = (s, d) => Math.round((Number(s) + 2 * Number(d)) / 3);
  const abpMap = map(v.ABP_Sys.current, v.ABP_Dia.current);
  const fico2 = Math.round(store.ventSettings?.fico2 ?? 0);
  const st = stLeads(store.rhythm, store.prevRhythm, store.rhythmProgress);

  // ── Gas analyser / MAC (bottom strip) ────────────────────────────────────────
  // Gas sampling requires a secured airway on the circuit. Not intubated → the
  // analyser reads 0 for every ET/FI (agent, O₂, N₂O) and MAC 0.
  const intubated = store.intubated;
  const ventNum = computeVentNumerics(store.ventSettings, parseAgeYears(store.patientAge));
  const AGENT_COLOR = { SEVO: '#FFD600', DES: '#29B6F6', ISO: '#B388FF', '—': '#8a93a0' };
  const agentLabel = ventNum.agentLabel || '—';
  const agentColor = AGENT_COLOR[agentLabel] || '#FFD600';
  const gas = intubated ? {
    etO2: Math.max(0, ventNum.fio2 - 5), fiO2: ventNum.fio2,
    etN2O: ventNum.etN2o, fiN2O: ventNum.n2o,
    etAgent: ventNum.agentPct, fiAgent: +(ventNum.agentPct * 1.12).toFixed(1),
    mac: ventNum.mac,
  } : { etO2: 0, fiO2: 0, etN2O: 0, fiN2O: 0, etAgent: 0, fiAgent: 0, mac: 0 };
  const nibpMap = nibpUnreadable ? '--' : map(store.nibpLastMeasured.sys, store.nibpLastMeasured.dia);
  const patientName = store.patientName?.trim() || 'DEMO PATIENT';
  // Sub line: patient ID + age + weight (falls back to "General" when empty)
  const patientSub = [
    store.patientId?.trim() || null,
    store.patientAge || null,
    store.patientWeight != null ? `${store.patientWeight} kg` : null,
  ].filter(Boolean).join(' · ') || 'General';

  return (
    <div className={styles.geRoot}>
      {Object.keys(store.instructorsSeen).length > 1 && !isPreview && (
        <div className={styles.dualInstructorWarn} role="alert">
          ⚠ Two instructor panels are controlling this monitor — close one, or they will overwrite each other.
        </div>
      )}
      <AlarmBar
        alarms={store.alarms}
        silenced={alarmSilenced}
        remainingSec={alarmSilenceRemaining}
        onSilenceToggle={isPreview ? undefined : toggleAlarmSilence}
      />

      {/* ── Patient bar ──────────────────────────────────────────────── */}
      <div className={styles.geTop}>
        <div className={styles.gePatient}>
          <span className={styles.geName}>{patientName}</span>
          <span className={styles.geSub}>{patientSub}</span>
        </div>
        <CaseTimer />
        {shocks.length > 0 && (() => {
          const defib = shocks.filter((s) => s.type === 'defibrillation').length;
          const cardiovert = shocks.filter((s) => s.type === 'cardioversion').length;
          const counts = [];
          if (defib) counts.push(`Defib ×${defib}`);
          if (cardiovert) counts.push(`Cardiovert ×${cardiovert}`);
          return (
            <span key={shocks[shocks.length - 1].at} className={styles.shockMarker}>
              <span className={styles.shockCount}>⚡ {counts.join(' · ')}</span>
              <span className={styles.shockTimes}>
                {shocks.slice(-6).map((s) => {
                  const prefix = s.type === 'defibrillation' ? 'D' : 'C';
                  const j = s.energy != null ? ` ${s.energy}J` : '';
                  return `${prefix}${j} ${new Date(s.at).toLocaleTimeString([], { hour12: false })}`;
                }).join(' · ')}
              </span>
            </span>
          );
        })()}
        <a
          className={styles.geGuide}
          href="/user-guide/index.html"
          target="_blank"
          rel="noopener noreferrer"
          title="Open the User Guide in a new tab"
        >
          User Guide ↗
        </a>
        <span className={`${styles.geConn} ${store.isConnected ? styles.geConnOk : ''}`}>
          {syncMode === 'websocket' ? 'WS' : '📡'}
        </span>
        <Clock />
      </div>

      <div className={styles.geBody}>
        {/* ── Waveforms ──────────────────────────────────────────────── */}
        <div className={styles.geWaves}>
          <GeWave name="II" sub="Monit. filter" scale="1 mV" type="ecg" tall />
          <GeWave name="V5" scale="0.5 mV" type="ecg" />
          <GeWave name="Pleth" scale="" type="spo2" />
          {ch.abp && <GeWave name="Art 1" scale="200/100/0" type="abp" />}
          {ch.etco2 && <GeWave name="CO₂" scale="40/0" type="capno" bufferDuration={12} samplesPerFrame={250 / 200} />}
          {ch.cvp && <GeWave name="CVP" scale="20/0" type="cvp" />}
        </div>

        {/* ── Numeric column — each box aligns with its waveform row ──── */}
        <div className={styles.geNumerics}>
          {/* HR ↔ II (tall) */}
          <GeBox label="HR" unit="/min" limits="40 ⌐ 160" color="#00E676"
            value={Math.round(v.HR.current)} sub="ECG" tall />

          {/* ST ↔ V5 */}
          <GeBox label="ST" unit="mm" color="#26C6DA" subValues={st} />

          {/* SpO₂ ↔ Pleth */}
          <GeBox label="SpO₂" unit="%" limits="90 ⌐ OFF" color="#FFFFFF"
            value={Math.round(v.SpO2.current)}>
            <span className={styles.geBeep}>✷✷✷</span>
          </GeBox>

          {/* Art 1 ↔ Art 1 */}
          {ch.abp && (
            <GeBox label="Art 1" unit="mmHg" limits="Sys 80↑180" color="#FF1744"
              value={`${Math.round(v.ABP_Sys.current)}/${Math.round(v.ABP_Dia.current)}`}
              sub={`(${abpMap})`} big={false} />
          )}

          {/* CO₂ ↔ CO₂ */}
          {ch.etco2 && (
            <GeBox label="CO₂" unit="mmHg" limits="ET 23↑46" color="#FFD600"
              subValues={[
                { label: 'ET', value: Math.round(v.EtCO2.current), color: '#FFD600' },
                { label: 'FI', value: fico2, color: '#FFD600' },
                { label: 'RR', value: Math.round(v.RR.current), color: '#FFD600' },
              ]} />
          )}

          {/* CVP ↔ CVP */}
          {ch.cvp && (
            <GeBox label="CVP" unit="mmHg" color="#26C6DA"
              value={Math.round(v.CVP.current)} big={false} />
          )}
        </div>
      </div>

      {/* ── Footer strip: T1 / Entropy / NMT / MAC / Gases / NIBP ──────── */}
      {/* Every box is always shown (like the real machine). A channel with no
          sensor connected reads "No sensor"; agent/gas ET·FI read 0 until the
          patient is intubated (capnography active). */}
      <div className={styles.geBottomStrip}>
        {/* Column 1 — T1 (top) over NMT (bottom) */}
        <div className={styles.geBottomCol}>
          {/* T1 — temperature */}
          <div className={styles.geBottomTile}>
            <div className={styles.geBoxHead}>
              <span className={styles.geBoxLabel} style={{ color: '#FFFFFF' }}>T1</span>
              <span className={styles.geBoxUnit}>°C</span>
              {!ch.temp && <span className={styles.geNoSensor}>No sensor detected</span>}
            </div>
            <span className={styles.geBoxValueSm} style={{ color: ch.temp ? '#FFFFFF' : '#6a7480' }}>
              {ch.temp ? v.Temp.current.toFixed(1) : '---'}
            </span>
          </div>

          {/* NMT (TOF) */}
          <div className={styles.geBottomTile}>
            <div className={styles.geBoxHead}>
              <span className={styles.geBoxLabel} style={{ color: '#E040FB' }}>NMT</span>
              {!ch.tof && <span className={styles.geNoSensor} style={{ color: '#E040FB' }}>Start measurement</span>}
            </div>
            {ch.tof ? (
              <>
                <div className={styles.geSubRow}>
                  <span className={styles.geSubVal}>
                    <span className={styles.geSubLabel} style={{ color: '#E040FB' }}>TOF%</span>
                    <span className={styles.geSubNum} style={{ color: '#E040FB' }}>{tofRatio}</span>
                  </span>
                  <span className={styles.geSubVal}>
                    <span className={styles.geSubLabel} style={{ color: '#E040FB' }}>Count</span>
                    <span className={styles.geSubNum} style={{ color: '#E040FB' }}>{tofCount}</span>
                  </span>
                </div>
                <TofBars count={tofCount} />
              </>
            ) : (
              <span className={styles.geBoxValueSm} style={{ color: '#6a7480' }}>✋</span>
            )}
          </div>
        </div>

        {/* Column 2 — Entropy (top) over MAC (bottom) */}
        <div className={styles.geBottomCol}>
          {/* Entropy (RE / SE) */}
          <div className={styles.geBottomTile}>
            <div className={styles.geBoxHead}>
              <span className={styles.geBoxLabel} style={{ color: ch.bis ? bisColor : '#8a93a0' }}>Entropy</span>
              {!ch.bis && <span className={styles.geNoSensor}>No sensor</span>}
            </div>
            <div className={styles.geSubRow}>
              <span className={styles.geSubVal}>
                <span className={styles.geSubLabel} style={{ color: '#00E676' }}>RE</span>
                <span className={styles.geSubNum} style={{ color: ch.bis ? '#00E676' : '#6a7480' }}>{ch.bis ? bisValue : '---'}</span>
              </span>
              <span className={styles.geSubVal}>
                <span className={styles.geSubLabel} style={{ color: '#FFD600' }}>SE</span>
                <span className={styles.geSubNum} style={{ color: ch.bis ? '#FFD600' : '#6a7480' }}>{ch.bis ? bisValue : '---'}</span>
              </span>
            </div>
          </div>

          {/* MAC — anaesthetic agent MAC */}
          <div className={styles.geBottomTile}>
            <div className={styles.geBoxHead}>
              <span className={styles.geBoxLabel} style={{ color: agentColor }}>MAC</span>
              <span className={styles.geBoxUnit}>{agentLabel} · age {ventNum.macAge}</span>
            </div>
            <div className={styles.geBoxMain}>
              <span className={styles.geBoxValueSm} style={{ color: agentColor }}>{gas.mac.toFixed(1)}</span>
              <span className={styles.geBoxSub} style={{ color: agentColor }}>MAC</span>
            </div>
          </div>
        </div>

        {/* Gases — ET / FI for O₂ and agent */}
        <div className={styles.geBottomTile}>
          <div className={styles.geBoxHead}>
            <span className={styles.geBoxLabel} style={{ color: '#FFFFFF' }}>Gases</span>
            <span className={styles.geBoxUnit}>% · Ext(A)</span>
          </div>
          <div className={styles.geGasGrid}>
            <span className={styles.geGasCorner} />
            <span className={styles.geGasCol} style={{ color: '#FFFFFF' }}>O₂</span>
            <span className={styles.geGasCol} style={{ color: '#29B6F6' }}>N₂O</span>
            <span className={styles.geGasCol} style={{ color: agentColor }}>{agentLabel}</span>
            <span className={styles.geGasRowLabel}>ET</span>
            <span className={styles.geGasVal} style={{ color: '#FFFFFF' }}>{Math.round(gas.etO2)}</span>
            <span className={styles.geGasVal} style={{ color: '#29B6F6' }}>{Math.round(gas.etN2O)}</span>
            <span className={styles.geGasVal} style={{ color: agentColor }}>{gas.etAgent.toFixed(1)}</span>
            <span className={styles.geGasRowLabel}>FI</span>
            <span className={styles.geGasVal} style={{ color: '#FFFFFF' }}>{Math.round(gas.fiO2)}</span>
            <span className={styles.geGasVal} style={{ color: '#29B6F6' }}>{Math.round(gas.fiN2O)}</span>
            <span className={styles.geGasVal} style={{ color: agentColor }}>{gas.fiAgent.toFixed(1)}</span>
          </div>
        </div>

        {/* NIBP */}
        <div className={`${styles.geBottomTile} ${styles.geBottomTileNibp}`}>
          <div className={styles.geBoxHead}>
            <span className={styles.geBoxLabel} style={{ color: '#FF1744' }}>NIBP</span>
            <span className={styles.geBoxUnit}>mmHg · Adult</span>
          </div>
          <span className={styles.geBoxValueSm} style={{ color: '#FF1744' }}>{nibpDisplay}</span>
          <span className={styles.geBoxSub} style={{ color: '#FF1744' }}>
            ({nibpMap}){nibpAuto ? ' · q2.5 min' : ''}
          </span>
        </div>
      </div>

      {/* ── Bottom menu bar ──────────────────────────────────────────── */}
      <div className={styles.geMenuBar}>
        <button className={`${styles.geMenuBtn} ${styles.geMenuHome}`}>⌂</button>
        {GE_MENU.map((m) => (
          <button
            key={m}
            className={`${styles.geMenuBtn} ${m.includes('Trends') && showTrends ? styles.geMenuBtnActive : ''}`}
            onClick={m.includes('Trends') ? () => setShowTrends(true) : undefined}
          >
            {m}
          </button>
        ))}
        <button
          className={`${styles.geMenuBtn} ${isCycling ? styles.geMenuBtnActive : ''}`}
          onClick={runNibpCycle}
          disabled={isCycling}
        >
          {isCycling ? 'NIBP\nCycling…' : 'NIBP\nStart'}
        </button>
        <button
          className={`${styles.geMenuBtn} ${nibpAuto ? styles.geMenuBtnActive : ''}`}
          onClick={() => setNibpAuto((a) => !a)}
        >
          {nibpAuto ? 'NIBP Auto\nStop' : 'NIBP Auto\nStart'}
        </button>
        <button className={styles.geMenuBtn}>{'Zero All\nPressures'}</button>
        <button className={`${styles.geMenuBtn} ${styles.geMenuAlarm}`}>🔔</button>
      </div>

      <MediaOverlay media={store.activeMedia} onClose={() => store.setActiveMedia(null)} />
      <CognitiveAidOverlay card={store.activeAidCard} />
      {showTrends && <MonitorTrends samples={trendSamples} onClose={() => setShowTrends(false)} />}
    </div>
  );
}

// ── Presentational helpers ────────────────────────────────────────────────────
function GeWave({ name, sub, scale, type, tall, bufferDuration, samplesPerFrame, children }) {
  return (
    <div className={`${styles.geWaveRow} ${tall ? styles.geWaveTall : ''}`}>
      <div className={styles.geWaveLabel}>
        <span className={styles.geLeadName}>{name}</span>
        {sub && <span className={styles.geLeadSub}>{sub}</span>}
        {scale && <span className={styles.geLeadScale}>{scale}</span>}
      </div>
      <div className={styles.geWaveCanvas}>
        <WaveformCanvas waveformType={type} bufferDuration={bufferDuration} samplesPerFrame={samplesPerFrame} />
      </div>
      {children}
    </div>
  );
}

function Clock() {
  const [t, setT] = useState(clockStr);
  useEffect(() => {
    const id = setInterval(() => setT(clockStr()), 1000);
    return () => clearInterval(id);
  }, []);
  return <span className={styles.geClock}>{t}</span>;
}

// Case elapsed timer — mirrors the instructor's Case Recording clock. Shown only
// while a case is active (state synced from the instructor: active + startedAt).
function CaseTimer() {
  const active = useSimStore((s) => s.caseRecording.active);
  const startedAt = useSimStore((s) => s.caseRecording.startedAt);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [active]);
  if (!active || !startedAt) return null;
  const s = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const mm = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, '0');
  const label = mm >= 60 ? `${Math.floor(mm / 60)}:${String(mm % 60).padStart(2, '0')}:${ss}` : `${mm}:${ss}`;
  return <span className={styles.geCaseTimer}>● CASE {label}</span>;
}
function clockStr() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function TofBars({ count }) {
  return (
    <div className={styles.tofBars}>
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className={styles.tofBar} style={{ opacity: i < count ? 1 : 0.2 }} />
      ))}
    </div>
  );
}

// Baseline ST deviation (mm) implied by a rhythm's morphology.
function stBaseFor(rhythm) {
  if (rhythm === 'st_elevation') return 2.5;
  if (rhythm === 'st_depression') return -1.8;
  return 0;
}

// Representative per-lead ST deviations (mm). The base is cross-faded between the
// previous and current rhythm using rhythmProgress — the SAME blend the waveform
// uses (generateWaveformBuffer) — so the numbers ramp gradually in lockstep with
// the morphology instead of snapping when an ST scenario is applied or reverted.
function stLeads(rhythm, prevRhythm = rhythm, rhythmProgress = 1) {
  const a = Math.max(0, Math.min(1, rhythmProgress));
  const base = stBaseFor(prevRhythm) * (1 - a) + stBaseFor(rhythm) * a;
  const j = (mult) => (base * mult).toFixed(1);
  return [
    { label: 'II',  value: j(1.0), color: '#00E676' },
    { label: 'V5',  value: j(1.1), color: '#26C6DA' },
    { label: 'aVL', value: j(0.4), color: '#FFD600' },
    { label: 'V2',  value: j(1.3), color: '#00E676' },
  ];
}
